import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnInit, Output, inject, signal } from '@angular/core';
import { FormBuilder, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { PlaceInListDto, TenantUsersApi } from '@buspro/web/apis';
import { PlacesApi } from '@buspro/web/apis';
import { AssignUserToTerminalDto } from '@buspro/web/apis';
import { ErrorDisplayComponent, ProblemDetail, useModalErrorHandler } from '@buspro/web/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { NzFormModule } from 'ng-zorro-antd/form';
import { NzModalModule } from 'ng-zorro-antd/modal';
import { NzNotificationService } from 'ng-zorro-antd/notification';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { lastValueFrom } from 'rxjs';

@Component({
  selector: 'tenant-assign-bus-stop-clerks-modal',
  standalone: true,
  imports: [
    CommonModule,
    NzFormModule,
    NzModalModule,
    NzSelectModule,
    ReactiveFormsModule,
    FormsModule,
    ErrorDisplayComponent,
  ],
  template: `
    <nz-modal
      (nzOnCancel)="onHide()"
      (nzOnOk)="update()"
      [nzClosable]="true"
      [nzOkDisabled]="false"
      [nzVisible]="show"
      [nzWidth]="400"
      nzOkText="Assign"
    >
      <div *nzModalTitle class="font-semibold">Assign Bus Stop Clerks</div>
      <div *nzModalContent>
        <app-error-display *ngIf="showError() && errorDetails" [error]="errorDetails" (dismiss)="onDismissError()" (retry)="update()"></app-error-display>
        <nz-form-item>
          <nz-form-label [nzRequired]="true" nzSpan="24"
            >Select Clerks for {{ busStop.name }} bus stop
          </nz-form-label>
          <nz-form-control nzSpan="24">
            <nz-select
              nzMode="multiple"
              nzShowSearch="true"
              [nzPlaceHolder]="'Choose...'"
              [nzLoading]="busStopClerksQuery.isLoading()"
              [(ngModel)]="listOfSelectedBusStopClerksIds"
            >
              <nz-option
                *ngFor="let clerk of busStopClerks"
                [nzLabel]="clerk.name"
                [nzValue]="clerk.id"
              >
              </nz-option>
            </nz-select>
            <ng-template #tagPlaceHolder let-selectedList
              >and {{ selectedList.length }} more selected
            </ng-template>
          </nz-form-control>
        </nz-form-item>
      </div>
    </nz-modal>
  `,
  styles: ``,
})
export class AssignBusStopClerksModalComponent implements OnInit {
  private formBuilder = inject(FormBuilder);
  private usersApi = inject(TenantUsersApi);
  private placesApi = inject(PlacesApi);
  private notification = inject(NzNotificationService);
  private modalErrorHandler = useModalErrorHandler();

  @Input()
  show = false;

  @Input()
  busStop!: PlaceInListDto;

  @Output()
  hide = new EventEmitter(true);

  @Output()
  assignmentCompleted = new EventEmitter<void>();

  form = this.formBuilder.group({
    busStopClerkIds: [[], [Validators.required]],
  });

  busStopClerksQuery = injectQuery(() => ({
    queryKey: ['bus-stop-clerks', this.busStop.id],
    queryFn: () =>
      lastValueFrom(
        this.usersApi.tenantUsersControllerGetAssignableBusStopClerksForBusStop({ busStopId: this.busStop.id }),
      ),
  }));

  listOfSelectedBusStopClerksIds: string[] = [];
  showError = signal(false);
  errorDetails: ProblemDetail | null = null;

  get busStopClerks() {
    return this.busStopClerksQuery.data() ?? [];
  }

  ngOnInit(): void {
    // Reset selection when modal opens - only unassigned clerks will be shown
    this.listOfSelectedBusStopClerksIds = [];
  }

  onHide() {
    this.hide.emit();
  }

  onDismissError() {
    this.showError.set(false);
    this.errorDetails = null;
  }

  async update() {
    if (!this.busStop) {
      this.notification.error('Error', 'No bus stop selected');
      return;
    }

    this.showError.set(false);
    this.errorDetails = null;

    try {
      // Assign each selected clerk to the bus stop
      const assignments = this.listOfSelectedBusStopClerksIds.map((clerkId) => {
        const payload: AssignUserToTerminalDto = {
          userId: clerkId,
          terminalId: this.busStop.id,
          forceReplace: false,
        };
        return lastValueFrom(this.usersApi.tenantUsersControllerAssignUserToTerminal({ id: clerkId, body: payload }));
      });

      await Promise.all(assignments);

      this.notification.success(
        '✅ Bus Stop Clerks Assigned',
        `Successfully assigned ${this.listOfSelectedBusStopClerksIds.length} clerk(s) to this bus stop.`,
      );
      this.assignmentCompleted.emit();
      this.onHide();
    } catch (error) {
      const result = this.modalErrorHandler.handleModalError(error, {
        modalName: 'assign-bus-stop-clerks',
        action: 'assign-bus-stop-clerks',
      });
      if (!result.shouldHandleGlobally) {
        this.showError.set(true);
        this.errorDetails = result.problemDetail;
        this.notification.error('❌ Assignment Failed', result.userMessage);
      }
    }
  }
}
